/**
 * CR-6439: Dependent-filter cascade logic for the PO Headers filter panel.
 *
 * Implements the same cascade behaviour as PO Lines (usePOLinesFilterCascades):
 *   - Division → Site (filter sites by selected division)
 *   - Company Country → Site (filter sites by selected country)
 *   - Site → Supplier (filter suppliers by selected site/customer)
 *   - Supplier Country → Supplier (filter suppliers by selected country)
 *   - Supplier Group → Supplier (filter suppliers by selected group)
 *   - Commodity Group → Commodity Type (filter types by selected group)
 *
 * The customer view panel uses these cascades to ensure filter dependencies
 * work correctly and don't regress the existing filter relationships.
 */
import { useMemo } from 'react';

export interface POHeaderFilterOptions {
  suppliers: Array<{
    id: number;
    name: string;
    group?: number | null;
    customers?: number[];  // Site IDs this supplier serves
    country?: string | null;  // Supplier's country for filtering
  }>;
  commodity_groups: Array<{ id: number; name: string }>;
  commodity_types: Array<{ id: number; name: string; commodity_group: number }>;
  supplier_groups: Array<{ id: number; name: string }>;
  sites: Array<{
    id: number;
    name: string;
    group?: number | null;  // Division ID
    country?: string | null;
  }>;
  /** CR-6446: Warehouse options for Site → Warehouse cascade. */
  warehouses: Array<{
    id: number;
    name: string;
    site: number;  // Parent site ID for cascade filtering
  }>;
  divisions: Array<{ id: number; name: string }>;
  site_countries: string[];
  supplier_countries: string[];
}

export interface POHeaderFilters {
  poNumber: string;
  supplier: string;
  customer: string;
  status: string;
  dateFrom: string;
  dateTo: string;
  site: string;
  /** CR-6446: Warehouse filter for Site → Warehouse cascade. */
  warehouse: string;
  division: string;
  companyCountry: string;
  supplierGroup: string;
  supplierCountry: string;
  commodityGroup: string;
  commodityType: string;
  pdfAttached: string;
  strategic: string;
}

export function usePOHeaderFilterCascades(
  filters: POHeaderFilters,
  filterOptions: POHeaderFilterOptions | null,
  onFilterChange: (key: string, value: string) => void,
) {
  const suppliers = filterOptions?.suppliers ?? [];
  const commodityTypes = filterOptions?.commodity_types ?? [];
  const sites = filterOptions?.sites ?? [];
  const warehouses = filterOptions?.warehouses ?? [];

  // Site options: narrowed by Division and/or Company Country.
  const siteOptions = useMemo(() => {
    let filtered = sites;

    // Filter by division if selected
    if (filters.division) {
      filtered = filtered.filter(
        (s) => String(s.group ?? '') === filters.division,
      );
    }

    // Filter by company country if selected
    if (filters.companyCountry) {
      filtered = filtered.filter(
        (s) => s.country === filters.companyCountry,
      );
    }

    // Keep currently selected site in the list even if it doesn't match
    if (filters.site && !filtered.some((s) => String(s.id) === filters.site)) {
      const selected = sites.find((s) => String(s.id) === filters.site);
      if (selected) return [selected, ...filtered];
    }
    return filtered;
  }, [sites, filters.division, filters.companyCountry, filters.site]);

  // Supplier options: narrowed by Site, Supplier Country, and/or Supplier Group.
  const supplierSiteOptions = useMemo(() => {
    let filtered = suppliers;

    // Filter by site if selected
    if (filters.site) {
      const siteId = parseInt(filters.site, 10);
      filtered = filtered.filter(
        (s) => s.customers?.includes(siteId) ?? false,
      );
    }

    // Filter by supplier country if selected
    if (filters.supplierCountry) {
      filtered = filtered.filter(
        (s) => s.country === filters.supplierCountry,
      );
    }

    // Filter by supplier group if selected
    if (filters.supplierGroup) {
      filtered = filtered.filter(
        (s) => String(s.group ?? '') === filters.supplierGroup,
      );
    }

    // Keep currently selected supplier in the list even if it doesn't match
    if (filters.supplier && !filtered.some((s) => s.name === filters.supplier)) {
      const selected = suppliers.find((s) => s.name === filters.supplier);
      if (selected) return [selected, ...filtered];
    }
    return filtered;
  }, [suppliers, filters.site, filters.supplierCountry, filters.supplierGroup, filters.supplier]);

  // CR-6446: Warehouse options: narrowed by the selected Site.
  const warehouseOptions = useMemo(() => {
    if (!filters.site) {
      return warehouses;
    }
    const siteId = parseInt(filters.site, 10);
    return warehouses.filter((w) => w.site === siteId);
  }, [warehouses, filters.site]);

  // Commodity Type: narrowed by the selected Commodity Group.
  const commodityTypeOptions = useMemo(() => {
    if (!filters.commodityGroup) {
      return commodityTypes;
    }
    return commodityTypes.filter(
      (t) => String(t.commodity_group) === filters.commodityGroup,
    );
  }, [commodityTypes, filters.commodityGroup]);

  // --- Cascade change handlers (clear now-invalid dependents) -------------

  /**
   * When Division changes, clear Site if it doesn't belong to the new division.
   */
  const handleDivisionChange = (value: string) => {
    onFilterChange('division', value);
    if (value && filters.site) {
      const selected = sites.find((s) => String(s.id) === filters.site);
      if (selected && String(selected.group ?? '') !== value) {
        onFilterChange('site', '');
        // CR-6446: Also clear warehouse and supplier since site was cleared
        onFilterChange('warehouse', '');
        onFilterChange('supplier', '');
      }
    }
  };

  /**
   * When Company Country changes, clear Site if it doesn't match the new country.
   */
  const handleCompanyCountryChange = (value: string) => {
    onFilterChange('companyCountry', value);
    if (value && filters.site) {
      const selected = sites.find((s) => String(s.id) === filters.site);
      if (selected && selected.country !== value) {
        onFilterChange('site', '');
        // CR-6446: Also clear warehouse and supplier since site was cleared
        onFilterChange('warehouse', '');
        onFilterChange('supplier', '');
      }
    }
  };

  /**
   * When Site changes, clear Supplier if it doesn't serve the new site,
   * and clear Warehouse if it doesn't belong to the new site (CR-6446).
   */
  const handleSiteChange = (value: string) => {
    onFilterChange('site', value);
    // CR-6446: Clear warehouse if it doesn't belong to the new site
    if (filters.warehouse) {
      const wh = warehouses.find((w) => String(w.id) === filters.warehouse);
      if (!value || !wh || String(wh.site) !== value) {
        onFilterChange('warehouse', '');
      }
    }
    if (value && filters.supplier) {
      const siteId = parseInt(value, 10);
      const selected = suppliers.find((s) => s.name === filters.supplier);
      if (selected && !(selected.customers?.includes(siteId) ?? false)) {
        onFilterChange('supplier', '');
      }
    }
  };

  /**
   * When Supplier Country changes, clear Supplier if it doesn't match the new country.
   */
  const handleSupplierCountryChange = (value: string) => {
    onFilterChange('supplierCountry', value);
    if (value && filters.supplier) {
      const selected = suppliers.find((s) => s.name === filters.supplier);
      if (selected && selected.country !== value) {
        onFilterChange('supplier', '');
      }
    }
  };

  /**
   * When Supplier Group changes, clear Supplier if it doesn't belong to the new group.
   */
  const handleSupplierGroupChange = (value: string) => {
    onFilterChange('supplierGroup', value);
    if (value && filters.supplier) {
      const selected = suppliers.find((s) => s.name === filters.supplier);
      if (selected && String(selected.group ?? '') !== value) {
        onFilterChange('supplier', '');
      }
    }
  };

  /**
   * When Commodity Group changes, clear Commodity Type if it doesn't belong to the new group.
   */
  const handleCommodityGroupChange = (value: string) => {
    onFilterChange('commodityGroup', value);
    if (value && filters.commodityType) {
      const selected = commodityTypes.find(
        (t) => String(t.id) === filters.commodityType,
      );
      if (selected && String(selected.commodity_group) !== value) {
        onFilterChange('commodityType', '');
      }
    }
  };

  return {
    siteOptions,
    warehouseOptions,
    supplierSiteOptions,
    commodityTypeOptions,
    handleDivisionChange,
    handleCompanyCountryChange,
    handleSiteChange,
    handleSupplierCountryChange,
    handleSupplierGroupChange,
    handleCommodityGroupChange,
  };
}
